import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Ship, Layers, ShieldCheck, Zap, FileText, Search, Calculator,
  GitCompareArrows, Send, ArrowRight, Globe, Timer, BadgePercent,
} from "lucide-react";
import Layout from "@/components/Layout";
import RateSearchPanel from "@/components/RateSearchPanel";
import { TRADE_LANES } from "@/lib/constants";

const HERO = "https://images.unsplash.com/photo-1585713181935-d5f622cc2415?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMGNvbnRhaW5lciUyMHNoaXAlMjBkcm9uZXxlbnwwfHx8fDE3ODY3MDI0MjJ8MA&ixlib=rb-4.1.0&q=85";
const TECH = "https://images.unsplash.com/photo-1590496793929-36417d3117de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGxvZ2lzdGljcyUyMG1hcCUyMHRlY2h8ZW58MHx8fHwxNzg2NzAyNDM2fDA&ixlib=rb-4.1.0&q=85";
const PORT = "https://images.unsplash.com/photo-1773809253612-7b1edf087313?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxidXN5JTIwcG9ydCUyMHRlcm1pbmFsfGVufDB8fHx8MTc4NjcwMjQyMnww&ixlib=rb-4.1.0&q=85";

const WHY = [
  { icon: GitCompareArrows, t: "Compare Multiple Lines", d: "See every carrier side by side on one screen." },
  { icon: Layers, t: "Ocean Freight + Local Charges", d: "Rates kept strictly separate, fully transparent." },
  { icon: Ship, t: "Container-wise Rates", d: "Dry and reefer, every size priced individually." },
  { icon: Timer, t: "Updated Rate Validity", d: "Expired rates auto-hidden. Always current." },
  { icon: ShieldCheck, t: "Transparent Structure", d: "No hidden costs — see the full breakdown." },
  { icon: Zap, t: "Faster Shipment Planning", d: "From search to enquiry in under a minute." },
];

const STEPS = [
  { icon: Search, t: "Search Route", d: "Pick export/import, POL, POD and container." },
  { icon: GitCompareArrows, t: "Compare Rates", d: "Rank lines by freight, total cost or transit." },
  { icon: Calculator, t: "Calculate Cost", d: "Multi-container estimate with local charges." },
  { icon: Ship, t: "Plan Shipment", d: "Lock the line and container plan you want." },
  { icon: Send, t: "Submit Enquiry", d: "Our pricing team confirms final booking rates." },
];

export default function Home() {
  const navigate = useNavigate();
  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Cargo container ship" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy/75" />
          <div className="absolute inset-0 hero-grid-fade opacity-60" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 py-16 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-mono-data text-signal bg-signal/10 border border-signal/30 px-3 py-1 rounded-sm mb-5">
              <Globe className="h-3.5 w-3.5" /> B2B SEA FREIGHT RATE PLATFORM
            </span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-5">
              Plan Your Shipment with Real-Time Sea Freight Rates
            </h1>
            <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl">
              Compare ocean freight, shipping lines, container options and local charges — all in one place.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 lg:mt-10 max-w-5xl">
            <RateSearchPanel dark />
          </motion.div>

          <div className="mt-8 flex flex-wrap gap-6 text-white/60 text-sm font-mono-data">
            <span>6+ Global Carriers</span>
            <span className="text-white/20">/</span>
            <span>13 Ports Worldwide</span>
            <span className="text-white/20">/</span>
            <span>Dry & Reefer Equipment</span>
          </div>
        </div>
      </section>

      {/* Popular trade lanes */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading font-bold text-3xl text-navy">Popular Trade Lanes</h2>
            <p className="text-muted-foreground mt-2">Jump straight into the routes exporters check most.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TRADE_LANES.map((lane, i) => (
            <motion.button
              key={lane.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              onClick={() => navigate(`/trade-lane/${lane.slug}`)}
              data-testid={`trade-lane-${i}`}
              className="group text-left bg-card border border-border rounded-md p-5 hover:border-signal hover:shadow-[4px_4px_0_0_#0A2540] transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono-data text-muted-foreground uppercase">{lane.region}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-signal group-hover:translate-x-1 transition-transform" />
              </div>
              <div className="font-heading font-bold text-xl text-navy mt-3">{lane.title}</div>
              <div className="text-sm text-muted-foreground mt-1 font-mono-data">{lane.pol} → {lane.pod}</div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Why use us */}
      <section className="bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mb-3">Why Use Our Rate Platform?</h2>
            <p className="text-white/60 mb-8 max-w-lg">
              Built as freight-rate technology, not a brochure. Everything an exporter needs to decide, fast.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              {WHY.map((w) => (
                <div key={w.t} className="flex gap-3">
                  <div className="h-9 w-9 shrink-0 bg-signal/15 border border-signal/30 rounded-sm flex items-center justify-center">
                    <w.icon className="h-4.5 w-4.5 text-signal" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">{w.t}</div>
                    <div className="text-sm text-white/55 mt-0.5">{w.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={TECH} alt="Logistics technology" className="rounded-md border border-white/10 w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-5 -left-5 bg-signal text-white p-5 rounded-md hidden sm:block">
              <div className="font-heading font-extrabold text-2xl">Delivering Trust</div>
              <div className="text-xs text-white/80 font-mono-data mt-1">Planning · Moving · Delivering</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <h2 className="font-heading font-bold text-3xl text-navy mb-2">How It Works</h2>
        <p className="text-muted-foreground mb-10">Five steps from route search to confirmed enquiry.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border rounded-md overflow-hidden">
          {STEPS.map((s, i) => (
            <div key={s.t} className="bg-card p-6 relative">
              <div className="font-mono-data text-signal text-sm mb-3">0{i + 1}</div>
              <s.icon className="h-6 w-6 text-navy mb-3" />
              <div className="font-heading font-bold text-navy">{s.t}</div>
              <div className="text-sm text-muted-foreground mt-1.5">{s.d}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/rates?shipment_type=export">
            <button className="bg-signal hover:bg-signal/90 text-white font-semibold px-6 py-3 rounded-sm transition-colors" data-testid="home-cta-rates">
              Check Freight Rates
            </button>
          </Link>
          <Link to="/enquiry">
            <button className="bg-navy hover:bg-navy/90 text-white font-semibold px-6 py-3 rounded-sm transition-colors" data-testid="home-cta-plan">
              Plan My Shipment
            </button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
